import { styled, css } from '../../../../modules';
import { keyframes } from 'styled-components';

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
    overflow: hidden;
`;

export const LogoSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 52px;
    max-width: 200px;
    object-fit: contain;
`;

export const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    width: 100%;
    overflow-y: auto;
    padding: 28px 20px 24px;
    gap: 20px;
`;

export const Title = styled.h1<{ $color: string }>`
    font-size: 24px;
    font-weight: 600;
    color: ${({ $color }) => $color};
    margin: 0;
    text-align: center;
    line-height: 1.3;
`;

export const DataCardContainer = styled.div`
    width: 100%;
    background-color: #ffffff;
    border: 1px solid #edf2f7;
    border-radius: 12px;
`;

export const DataRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #edf2f7;

    &:last-child {
        border-bottom: none;
    }
`;

export const RowLabel = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: #718096;
    flex: 1;
    padding-right: 8px;
`;

export const RowValue = styled.span<{ $color: string; $bold?: boolean }>`
    font-size: 13px;
    font-weight: ${({ $bold }) => ($bold ? 700 : 600)};
    color: ${({ $color }) => $color};
    text-align: right;
`;

export const InfoCallout = styled.div`
    width: 100%;
    background-color: #faf7f4;
    border-radius: 8px;
    border: 1px solid #ede8e2;
    padding: 12px 14px;
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: flex-start;
`;

export const InfoIcon = styled.div<{ $color: string }>`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
`;

export const InfoText = styled.p<{ $color: string }>`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    line-height: 1.5;

    a {
        color: ${({ $color }) => $color};
        text-decoration: underline;
        cursor: pointer;
    }
`;

export const CheckboxWrapper = styled.div`
    width: 100%;
`;

export const Footer = styled.footer`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    border-top: 1px solid #e5e5e5;
    background-color: #ffffff;
    flex-shrink: 0;
`;

export const SubmitButton = styled.button<{ $backgroundColor: string; $disabled: boolean }>`
    width: 100%;
    padding: 16px 20px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $backgroundColor, $disabled }) => ($disabled ? '#aaa' : $backgroundColor)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

// ── Bottom sheet ──────────────────────────────────────────

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to   { opacity: 0; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const slideDown = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(100%); }
`;

export const ModalOverlay = styled.div<{ $closing?: boolean }>`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${fadeOut} 0.3s ease forwards;`
            : css`animation: ${fadeIn} 0.3s ease forwards;`}
`;

export const ModalSheet = styled.div<{ $closing?: boolean }>`
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 28px 24px 36px;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${slideDown} 0.3s ease forwards;`
            : css`animation: ${slideUp} 0.3s ease forwards;`}
`;

export const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: #ddd;
    align-self: center;
    margin-bottom: 4px;
`;

export const ModalTitle = styled.h2<{ $color: string }>`
    font-size: 20px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 0;
`;

export const ModalText = styled.p`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    line-height: 1.6;
`;

export const ModalButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
`;
